import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * The one footer of every form and side panel (audit M11, M12): the reason a save is blocked on the
 * left, «Annulla» then the primary on the right, 44 px high on a phone. «Annulla» and the primary
 * stay together: when the line is too short, the pair goes under the reason, never the primary
 * alone. Without `onPrimary` the primary submits the form named by `form`.
 *
 * `sticky` keeps it in view on a long page. `'page'` rests above the phone's tab bar while the
 * shell shows one (the container rule of `.shell-tabbar`, ≤619 px) and at the bottom edge
 * otherwise. `'panel'` is for a dialog or sheet: always the bottom edge, since no tab bar is under it.
 */
export function FormFooter({
  reason,
  primaryLabel,
  primaryDisabled = false,
  onPrimary,
  form,
  cancelLabel,
  onCancel,
  cancelTo,
  cancelDisabled = false,
  sticky,
  className,
  children,
}: {
  /** Why the primary is disabled, in words; shown only while it is. */
  readonly reason?: string | null;
  readonly primaryLabel: string;
  readonly primaryDisabled?: boolean;
  readonly onPrimary?: () => void;
  readonly form?: string;
  readonly cancelLabel: string;
  readonly onCancel?: () => void;
  readonly cancelTo?: string;
  readonly cancelDisabled?: boolean;
  readonly sticky?: 'page' | 'panel';
  readonly className?: string;
  /** Extra controls kept left of «Annulla» (rare). */
  readonly children?: ReactNode;
}) {
  const reasonId = useId();
  const showReason = primaryDisabled && reason != null && reason !== '';
  return (
    <div
      data-slot="form-footer"
      className={cn(
        'border-border flex flex-wrap items-center justify-end gap-x-4 gap-y-2 border-t pt-4',
        sticky && 'bg-background sticky bottom-0 z-10 pb-2',
        sticky === 'page' &&
          '@max-[620px]/shell:bottom-[calc(80px+env(safe-area-inset-bottom,0px))]',
        className,
      )}
    >
      {showReason ? (
        <p id={reasonId} className="text-muted-foreground min-w-[min(100%,16rem)] flex-1 text-sm">
          {reason}
        </p>
      ) : null}
      <div data-slot="form-footer-actions" className="ml-auto flex shrink-0 items-center gap-2">
        {children}
        {cancelTo ? (
          <Button asChild variant="outline" className="min-h-11">
            <Link to={cancelTo}>{cancelLabel}</Link>
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            disabled={cancelDisabled}
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          type={onPrimary ? 'button' : 'submit'}
          {...(onPrimary || form === undefined ? {} : { form })}
          className="min-h-11"
          disabled={primaryDisabled}
          aria-describedby={showReason ? reasonId : undefined}
          onClick={onPrimary}
        >
          {primaryLabel}
        </Button>
      </div>
    </div>
  );
}
